import { useId, useState } from 'react'
import axios from 'axios'
import { AlertTriangle, CheckCircle2 } from 'lucide-react'

import { Modal } from 'components/UI/Modal'
import {
  useAgentPreflight,
  useCreateCampaignColumn,
  useListCampaignColumns,
  useListAgentTypes,
} from 'services/campaignColumns/hooks'
import type { AgentPreflight } from 'services/campaignColumns/types'

import { AgentTypeSelect } from './AgentTypeSelect'
import {
  MANUAL_COLUMN_AGENT_TYPE,
  MANUAL_COLUMN_META,
} from './agentTypeMetadata'
import { SettingsForm } from './SettingsForm'

interface Props {
  campaignId: number
  campaignProductId?: number | null
  onClose: () => void
}

const RESERVED_TEMPLATE_VARIABLES = new Set([
  'first_name',
  'last_name',
  'email',
  'company',
  'title',
])

function slugifyColumnName(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
  const variableSlug = /^\d/.test(slug) ? `column_${slug}` : slug
  return variableSlug.slice(0, 64).replace(/_+$/g, '')
}

function getCreateColumnErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data
    if (data && typeof data === 'object') {
      const detail = (data as { detail?: unknown; error_message?: unknown })
        .detail
      if (typeof detail === 'string' && detail) return detail
      const errorMessage = (
        data as { detail?: unknown; error_message?: unknown }
      ).error_message
      if (typeof errorMessage === 'string' && errorMessage) {
        return errorMessage
      }
    }
  }
  return error instanceof Error ? error.message : 'Failed to create column'
}

export function AddAgentColumnModal({
  campaignId,
  campaignProductId = null,
  onClose,
}: Props) {
  const [selectedAgentType, setSelectedAgentType] = useState<string | null>(
    null
  )
  const [name, setName] = useState('')
  const [nameTouched, setNameTouched] = useState(false)
  const [settings, setSettings] = useState<Record<string, unknown>>({})
  const [error, setError] = useState<string | null>(null)
  const columnNameInputId = useId()

  const { data: agentTypesData } = useListAgentTypes(campaignId)
  const { data: columnsData } = useListCampaignColumns(campaignId)
  const createColumn = useCreateCampaignColumn(campaignId)

  // Predefined agents only — the free-form custom agent path is parked for MVP
  // (backend `custom_prompt` engine still exists; just no UI surface).
  const agentTypes = [
    MANUAL_COLUMN_META,
    ...(agentTypesData?.results ?? []).filter((a) => !a.is_custom),
  ]
  const selectedAgent =
    agentTypes.find((a) => a.agent_type === selectedAgentType) ?? null
  const isManualColumn = selectedAgentType === MANUAL_COLUMN_AGENT_TYPE
  const selectedProductIds = selectedAgent
    ? selectedAgent.settings_schema.flatMap((field) => {
        if (field.type !== 'multiselect_products') return []
        const value = settings[field.key]
        return Array.isArray(value)
          ? value.filter((id): id is number => typeof id === 'number')
          : []
      })
    : []
  const preflightAgentType = isManualColumn ? null : selectedAgentType
  const { data: preflightData, isFetching: isFetchingPreflight } =
    useAgentPreflight(campaignId, preflightAgentType, selectedProductIds)
  const existingSlugs = new Set(
    (columnsData?.results ?? []).map((column) => column.merge_variable_slug)
  )
  const proposedSlug = slugifyColumnName(name)
  const reservedSlug = proposedSlug
    ? RESERVED_TEMPLATE_VARIABLES.has(proposedSlug)
    : false
  const duplicateSlug = proposedSlug ? existingSlugs.has(proposedSlug) : false

  const buildInitialSettings = (
    settingsSchema: NonNullable<typeof selectedAgent>['settings_schema']
  ) => {
    if (!campaignProductId) return {}

    return settingsSchema.reduce<Record<string, unknown>>((acc, field) => {
      if (field.type === 'multiselect_products') {
        acc[field.key] = [campaignProductId]
      }
      return acc
    }, {})
  }

  const handleAgentSelect = (agentType: string) => {
    setSelectedAgentType(agentType)
    const meta = agentTypes.find((a) => a.agent_type === agentType)
    if (meta && !nameTouched) {
      setName(meta.name)
    }
    setSettings(meta ? buildInitialSettings(meta.settings_schema) : {})
  }

  const canSave = (() => {
    if (!selectedAgent) return false
    if (!name.trim()) return false
    if (!proposedSlug || reservedSlug || duplicateSlug) return false
    return selectedAgent.settings_schema
      .filter((f) => f.required)
      .every((f) => {
        const v = settings[f.key]
        if (Array.isArray(v)) return v.length > 0
        if (typeof v === 'string') return v.trim().length > 0
        return v !== undefined && v !== null
      })
  })()

  const handleCreate = async () => {
    if (!selectedAgent) return
    setError(null)
    try {
      await createColumn.mutateAsync({
        agent_type: selectedAgent.agent_type,
        name,
        settings,
      })
      onClose()
    } catch (e) {
      setError(getCreateColumnErrorMessage(e))
    }
  }

  return (
    <Modal show onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add Column</Modal.Title>
      </Modal.Header>
      <Modal.Body className="space-y-4 px-6 pb-5 pt-3">
        <p className="text-sm text-fg-secondary">
          Select a column type. AI columns preview on the first 5 rows; manual
          columns start empty for you to fill in per contact.
        </p>

        <AgentTypeSelect
          agentTypes={agentTypes}
          selected={selectedAgentType}
          onSelect={handleAgentSelect}
        />

        {selectedAgent && (
          <>
            {isManualColumn ? (
              <div className="rounded-md border border-line bg-surface px-3 py-2 text-xs text-fg-secondary">
                This column starts empty. Enter a value for each contact before
                using this variable in an email.
              </div>
            ) : (
              <AgentPreflightNotice
                preflight={preflightData}
                isLoading={isFetchingPreflight}
              />
            )}
            <div>
              <label
                htmlFor={columnNameInputId}
                className="block text-sm font-medium text-fg"
              >
                Column Name
              </label>
              <input
                id={columnNameInputId}
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                  setNameTouched(true)
                }}
                className="mt-1 w-full rounded-md border border-line-form px-3 py-2 text-sm text-fg focus:outline-none focus:ring-1 focus:ring-line-focus"
              />
              <ColumnVariableHint
                slug={proposedSlug}
                isReserved={reservedSlug}
                isDuplicate={duplicateSlug}
              />
            </div>
            {!isManualColumn && (
              <SettingsForm
                agent={selectedAgent}
                settings={settings}
                onChange={setSettings}
                campaignProductId={campaignProductId}
              />
            )}
          </>
        )}

        <p className="text-xs text-fg-secondary">
          {!selectedAgent
            ? 'Choose a column type to continue.'
            : isManualColumn
              ? 'Manual columns do not run AI. Empty rows must be filled manually before using the variable.'
              : 'Will auto-run on first 5 rows for preview. You can then approve to run on all rows.'}
        </p>

        {error && (
          <div className="rounded-md border !border-alert-danger-border bg-alert-danger-bg p-3 text-sm text-alert-danger-title">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-line bg-surface px-3 py-2 text-sm text-fg hover:bg-surface-hover"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={!canSave || createColumn.isPending}
            className="rounded-md bg-brand px-3 py-2 text-sm text-fg-inverse hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {createColumn.isPending
              ? 'Creating…'
              : isManualColumn
                ? 'Create Column'
                : 'Create & Preview'}
          </button>
        </div>
      </Modal.Body>
    </Modal>
  )
}

function ColumnVariableHint({
  slug,
  isReserved,
  isDuplicate,
}: {
  slug: string
  isReserved: boolean
  isDuplicate: boolean
}) {
  if (!slug) {
    return (
      <p className="mt-1 text-xs text-warning-strong">
        Use at least one letter or number so this column can be used as an email
        variable.
      </p>
    )
  }
  if (isReserved) {
    return (
      <p className="mt-1 text-xs text-warning-strong">
        This name would conflict with a standard email variable. Choose a more
        specific column name.
      </p>
    )
  }
  if (isDuplicate) {
    return (
      <p className="mt-1 text-xs text-warning-strong">
        A campaign column already uses this email variable. Choose a different
        name.
      </p>
    )
  }
  return (
    <p className="mt-1 text-xs text-fg-secondary">
      Email variable:{' '}
      <code className="rounded bg-surface-hover px-1.5 py-0.5 text-xxs text-fg">
        {`{${slug.toUpperCase()}}`}
      </code>
    </p>
  )
}

function AgentPreflightNotice({
  preflight,
  isLoading,
}: {
  preflight?: AgentPreflight
  isLoading: boolean
}) {
  if (isLoading && !preflight) {
    return (
      <div className="rounded-md border border-line bg-surface px-3 py-2 text-xs text-fg-secondary">
        Checking campaign readiness…
      </div>
    )
  }
  if (!preflight || preflight.total_contacts === 0) return null

  if (preflight.missing_contacts === 0) {
    return (
      <div className="grid grid-cols-[16px_1fr] gap-2 rounded-md border !border-alert-success-border bg-alert-success-bg px-3 py-2 text-xs text-alert-success-title">
        <CheckCircle2 className="mt-0.5 size-3.5" aria-hidden="true" />
        <span className="min-w-0 leading-5">
          All {preflight.total_contacts} contacts have the context this agent
          needs.
        </span>
      </div>
    )
  }

  return (
    <div className="rounded-md border border-warning-border bg-warning-bg-subtle px-3 py-2 text-xs text-fg-secondary">
      <div className="grid grid-cols-[16px_1fr] gap-2">
        <AlertTriangle
          className="mt-0.5 size-3.5 text-warning"
          aria-hidden="true"
        />
        <div className="min-w-0 leading-5">
          <p className="font-medium">
            {preflight.missing_contacts} of {preflight.total_contacts} contacts
            may need manual values.
          </p>
          <p className="mt-0.5">
            Those rows are missing context this agent needs, so they may come
            back blank and require manual entry before using the variable in an
            email.
          </p>
        </div>
      </div>
      {preflight.requirements.length > 0 && (
        <div className="mt-2 space-y-1 pl-5">
          {preflight.requirements.map((requirement) => (
            <div key={requirement.key}>
              <span className="font-medium">
                {requirement.missing_count} missing {requirement.label}
              </span>
              <span>: {requirement.message}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
